import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

const TOOLTIP_GAP = 8;
const CARET_SIZE = 6;

/**
 * How long an interactive panel stays up after the pointer leaves the trigger.
 * It has to outlast the trip across the caret gap, or the panel closes out from
 * under a reader on their way to the control inside it.
 */
const INTERACTIVE_CLOSE_DELAY_MS = 160;

type HeaderTooltipProps = {
  children: ReactNode;
  tooltip: ReactNode;
  className?: string;
  /** Override the tooltip panel max-width (defaults to max-w-[336px]). */
  panelClassName?: string;
  /**
   * Set when the tooltip contains something to click. A default tooltip is
   * `pointer-events-none` and closes the moment the pointer leaves the trigger,
   * which makes any control inside it unreachable — the cursor can never land
   * on it. Opting in makes the panel hoverable and delays the close.
   *
   * Left off by default because it costs a reader something: an interactive
   * panel sits over the page and swallows hover on whatever is behind it, and
   * that's only worth paying for where there's a control to reach.
   */
  interactive?: boolean;
};

/**
 * Hover/focus tooltip that renders its children as the trigger (unlike
 * InsightsSectionTitle, which forces a DottedLabel section-title style). Used for
 * the Ad-performance-by-month column headers and the sales-to-date lift tag.
 */
export function HeaderTooltip({
  children,
  tooltip,
  className,
  panelClassName = "max-w-[336px]",
  interactive = false,
}: HeaderTooltipProps) {
  const tooltipId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = tooltipRef.current;
    if (!trigger || !panel) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const left = triggerRect.left + triggerRect.width / 2 - panelRect.width / 2;
    const top = triggerRect.top - panelRect.height - TOOLTIP_GAP - CARET_SIZE;

    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) {
      return;
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, updatePosition]);

  const cancelClose = useCallback(() => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  // A pending close would otherwise fire against an unmounted component when a
  // row re-renders or a filter swaps the trigger out mid-hover.
  useEffect(() => cancelClose, [cancelClose]);

  const show = () => {
    cancelClose();
    setIsVisible(true);
  };

  const hide = () => {
    if (!interactive) {
      setIsVisible(false);
      return;
    }
    cancelClose();
    closeTimer.current = window.setTimeout(
      () => setIsVisible(false),
      INTERACTIVE_CLOSE_DELAY_MS
    );
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex cursor-default ${className ?? ""}`.trim()}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        aria-describedby={isVisible ? tooltipId : undefined}
      >
        {children}
      </span>

      {isVisible
        ? createPortal(
            <div
              ref={tooltipRef}
              id={tooltipId}
              role="tooltip"
              className={`fixed z-[700] ${
                interactive ? "pointer-events-auto" : "pointer-events-none"
              }`}
              style={{ top: coords.top, left: coords.left }}
              onMouseEnter={interactive ? show : undefined}
              onMouseLeave={interactive ? hide : undefined}
            >
              <div
                className={`text-fs-text-primary-inverse relative rounded-[4px] bg-[#333333] px-4 py-3 text-left text-[14px] leading-[20px] font-normal shadow-[0_2px_8px_rgba(0,0,0,0.2)] ${panelClassName}`}
              >
                {tooltip}
                <span
                  aria-hidden
                  className="absolute bottom-[-6px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-[6px] border-t-[6px] border-x-transparent border-t-[#333333]"
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
